import { Fragment, type MouseEventHandler, useMemo } from 'react';
import { useSurface } from '@/contexts/SurfaceContext';
import { parseBroadcastBodyToSegments } from '@/utils/broadcastLinkMarkup';

interface BroadcastMessageBodyProps {
  text: string;
  className?: string;
  linkClassName?: string;
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
}

/**
 * An admin broadcast's text with its `<link>` tags. The links are external (http(s) only), so they
 * are anchors only where `can('externalLinks')`; on a kiosk or an unknown surface the label stays
 * as plain text and nothing leaves the app (spec 2026-10-08-kiosk-web-split §2).
 */
export default function BroadcastMessageBody({
  text,
  className,
  linkClassName,
  onLinkClick,
}: BroadcastMessageBodyProps) {
  const segments = useMemo(() => parseBroadcastBodyToSegments(text), [text]);
  const linksAllowed = useSurface().can('externalLinks');
  return (
    <span className={className}>
      {segments.map((seg, i) =>
        seg.type === 'text' ? (
          <Fragment key={i}>{seg.text}</Fragment>
        ) : !linksAllowed ? (
          <Fragment key={i}>{seg.label}</Fragment>
        ) : (
          <a
            key={i}
            href={seg.href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
            onClick={onLinkClick}
          >
            {seg.label}
          </a>
        ),
      )}
    </span>
  );
}
